<template>
    <div class="table-container">
        <div class="header">
            <div class="title">{{ title }}</div>
            <fieldset class="filters">
                <legend>Filters</legend>
                <v-text-field v-model="filters.recipient" background-color="white" label="Recipient" />
                <v-select
                    v-model="filters.status"
                    background-color="white"
                    :items="statusOptions"
                    label="Status"
                />
                <div style="position: relative;">
                    <label
                        class="v-label v-label--active theme--light date-picker"
                        for="sent-date-picker"
                    >
                        Sent Date
                    </label>
                    <date-picker
                        v-model="filters.sentDate"
                        range
                        type="datetime"
                        :input-attr="{ id: 'sent-date-picker' }"
                        @clear="clearSentDate"
                    />
                </div>
                <button-search :disabled="isFetchingTrans" :callback="fetchAllMessages" />
            </fieldset>
        </div>
        <array-view
            :items="items"
            :headers="listHeaders"
            :total-pages="totalPages"
            :page="page"
            :size="size"
            :total-items="totalItems"
            :show-icons="false"
            :page-size-options="pageSizeOptions"
            :is-fetching="isFetchingTrans"
            should-rows-expand
            @set-active-item="setActiveItem"
            @paginationChange="paginationChange"
        />
        <transactional-popup-dialog
            v-if="activeItem"
            :title="title"
            :is-sms="isSms"
            :active-item="activeItem"
            :headers="detailHeaders"
            @close-dialog="closeDialog"
        />
    </div>
</template>

<script>
    import 'vue2-datepicker/index.css'

    import ArrayView from '@/components/ArrayView.vue'
    import transactionalViberService from '@/services/transactional-viber-service'
    import transactionalSmsService from '@/services/transactional-sms-service'
    import { SMS_STATUS_LABELS, VIBER_STATUS_LABELS } from '@/utils/consts'
    import TransactionalPopupDialog from '@/components/TransactionalPopupDialog.vue'
    import dateTimeUtils from '@/utils/date-time-utils'
    import DatePicker from 'vue2-datepicker'
    import ButtonSearch from '@/components/ButtonSearch.vue'
    import tableUtils from '@/utils/tableUtils'

    export default {
        components: { ButtonSearch, ArrayView, DatePicker, TransactionalPopupDialog },
        data() {
            return {
                items: [],
                commonHeaders: [],
                totalPages: 0,
                page: 1,
                size: 15,
                totalItems: 0,
                filters: {
                    sentDate: [],
                    recipient: '',
                    status: 'ALL'
                },
                isPickerOpen: false,
                activeItem: null,
                pageSizeOptions: [15, 50, 100],
                isFetchingTrans: false
            }
        },
        computed: {
            isSms() {
                return this.$route.name === 'Transactional SMS'
            },
            title() {
                return `Transactional ${this.isSms ? 'SMS' : 'Viber'}`
            },
            service() {
                return this.isSms ? transactionalSmsService : transactionalViberService
            },
            statusOptions() {
                return [
                    'ALL',
                    ...(this.isSms ? SMS_STATUS_LABELS : VIBER_STATUS_LABELS)
                ]
            },
            filtersToApply() {
                const filters = {}
                if (this.filters.recipient || this.filters.sentDate.length === 2 || this.filters.status !== 'ALL') {
                    filters.filter = {}
                }
                if (this.filters.recipient) {
                    filters.filter.recipient = this.filters.recipient
                }
                if (this.filters.sentDate.length === 2) {
                    filters.filter.sentDateGte = dateTimeUtils.getFormattedDateForSubmit(this.filters.sentDate[0])
                    filters.filter.sentDateLte = dateTimeUtils.getFormattedDateForSubmit(this.filters.sentDate[1])
                }
                if (this.filters.status !== 'ALL') {
                    filters.filter.status = this.filters.status
                }
                filters.range = [this.page - 1, this.size]
                return filters
            },
            listHeaders() {
                return [
                    { text: '#', value: 'index' },
                    ...this.commonHeaders,
                    { text: 'Actions', value: 'actions', align: 'center' },
                    { text: '', value: 'data-table-expand' }
                ]
            },
            detailHeaders() {
                return this.isSms ? this.commonHeaders : [
                    ...this.commonHeaders,
                    { text: 'Button Text', value: 'buttonCaption' },
                    { text: 'Button URL', value: 'buttonUrl' },
                    { text: 'Image URL', value: 'imageUrl' }
                ]
            }
        },
        watch: {
            isSms: {
                handler() {
                    this.initializeHeaders()
                    this.fetchAllMessages()
                }
            }
        },
        created() {
            this.page = this.$route.query.page ? parseInt(this.$route.query.page) : this.page
            this.initializeHeaders()
            this.fetchAllMessages()
        },
        methods: {
            initializeHeaders() {
                const newHeaders = [
                    { text: 'Id', value: 'id' },
                    { text: 'Recipient', value: 'recipient' },
                    { text: 'Sent Date', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Status Date', value: 'statusDate' },
                    { text: 'Country', value: 'country' }
                ]
                if (this.isSms) {
                    newHeaders.splice(1, 0, { text: 'Originator', value: 'originator' })
                    newHeaders.splice(6, 0, { text: 'Parts', value: 'parts' })
                    newHeaders.push({ text: 'UTF-8', value: 'utf8' })
                } else {
                    newHeaders.splice(4, 0, { text: 'Status Details', value: 'statusDetails' })
                }
                this.commonHeaders = newHeaders
            },
            paginationChange(page, size) {
                this.size = size
                this.page = page
                this.fetchAllMessages()
            },
            fetchAllMessages(shouldChangePage = false) {
                if (shouldChangePage) {
                    this.page = 1
                }
                this.isFetchingTrans = true
                this.service.getTransactionalMessages(this.filtersToApply).then(resp => {
                    this.totalPages = resp.data.totalPages
                    this.items = resp.data.content
                        .map((item, index) => ({
                            ...item,
                            statusDate: item.statusDate && dateTimeUtils.getHumanizedDateTime(item.statusDate, true),
                            index: index + (this.page - 1) * this.size + 1,
                            details: this.getMessageDetails(item)
                        }))
                    this.totalItems = resp.data.totalElements
                }).finally(() => {
                    this.isFetchingTrans = false
                })
            },
            setActiveItem(item) {
                this.activeItem = item
            },
            closeDialog() {
                this.activeItem = null
            },
            clearSentDate() {
                this.filters.sentDate = []
            },
            getMessageDetails(message) {
                return tableUtils.getMessageDetails(message)
            }
        }
    }
</script>

<style scoped lang="scss">
.header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.filters {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;

    legend {
        font-weight: 900;
    }
}

.date-picker {
    font-size: 0.8rem;
    top: -15px;
    left: 0;
    right: auto;
    position: absolute;
}

.form-wrapper label {
    font-size: 14px;
}

@media (max-width: 992px) {
    .header {
        flex-direction: column;
    }

    .filters {
        flex-direction: column;
        align-items: center;
        justify-content: center;

        *:not(button,legend) {
            width: 100%;
        }
    }
}
</style>
